/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/data-review-table.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import { useLocalState, useTranslation } from 'hooks';
import { DataReviewTableColumnType } from 'types/graphql-global-types';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import AuditEnumValues from 'views/pages/admin/audit-log/definitions/enums';
import { getFieldChangeItems, getProcessedFieldChangeItems } from 'views/pages/admin/audit-log/service';
import Constants from '@utils/constants';
import { GET_PANELS } from '@queries/central-monitoring';
import { getPanels, getPanelsVariables } from '@queries/generated/central-monitoring.generated';

const DataReviewTableRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    const { t } = useTranslation();

    const { state: localState } = useLocalState();
    const { data: panels } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        variables: {
            organisationId: localState?.selectedOrganisation?.id,
            studyId: Constants.ObjectId.Empty,
            timestamp: newItem?.updated ?? '',
        },
        fetchPolicy: 'no-cache',
    });

    const getColumnValue = (recordData: any, item: any) => {
        let value = '';
        let type = '';
        const panelId = recordData?.PanelId?.$oid ?? Constants.ObjectId.Empty;
        for (const property in recordData) {
            if (property.toLowerCase().match('heading')) {
                value += `Column Heading: ${recordData[property]}; `;
            } else if (property.toLowerCase().match('type')) {
                type = AuditEnumValues.ColumnType[recordData[property]];
                value += `Column Type: ${t(`auditLog:${type}`)}; `;
            } else if (property.toLowerCase().match('level') && recordData[property] !== 0) {
                const level = AuditEnumValues.ColumnLevelType[recordData[property]];
                value += `${property}: ${t(`auditLog:${level}`)}; `;
            } else if (property.toLowerCase() === 'panelid') {
                if (panelId !== Constants.ObjectId.Empty) {
                    const source = panels?.panels?.find((el: any) => el.id === panelId)?.graph?.plotTitle;
                    value += `${t('auditLog:PANEL_NAME')}: ${source}; `;
                }
            } else if (property.toLowerCase() === 'panelfield') {
                if (panelId !== Constants.ObjectId.Empty) {
                    const source = AuditEnumValues.PanelFieldType[recordData[property]];
                    value += `${t(`auditLog:${property}`)}: ${t(`auditLog:${source}`)}`;
                }
            } else if (property.toLowerCase() === 'rowdatasource') {
                const source = AuditEnumValues.DataSourceTableType[recordData[property]];
                value += `${property}: ${source}; `;
            } else if (
                recordData[property] &&
                `${recordData[property]}`.length &&
                !property.toLowerCase().match('datasource')
            ) {
                if (property.toLowerCase() === 'datafield')
                    value += `${type === DataReviewTableColumnType.STATIC ? 'Data Point' : 'Calculation'}: ${
                        t(`auditLog:${recordData[property]}`) || '--'
                    }; `;
                else value += `${t(`auditLog:${property}`)}: ${t(`auditLog:${recordData[property]}`) || '--'}; `;
            }
        }
        return value.replace(/;(\s+)?$/, '');
    };

    const refactoredOldRecord = {
        ...oldRecord,
        DataBasedOn: oldRecord.Type,
        TableDataSource: oldRecord.DataSource,
        DataSource: null,
        Type: null,
        ColumnsAdded: null,
        ColumnsRemoved:
            oldRecord.Columns?.filter(
                (item: any) =>
                    Boolean(item) &&
                    !newRecord.Columns.find((el: any) => el && item.ColumnId?.$oid === el.ColumnId?.$oid),
            ).map((item: any) => ({ ...item, ColumnId: null })) ?? [],
        ColumnsUpdated: oldRecord.Columns?.filter(
            (item: any) =>
                Boolean(item) && newRecord.Columns.find((el: any) => el && item.ColumnId?.$oid === el.ColumnId?.$oid),
        ).map((item: any) => ({ ...item, ColumnId: null })),
        Columns: null,
        CfId: oldRecord.CfId?.$oid,
    };

    const refactoredNewRecord = {
        ...newRecord,
        DataBasedOn: newRecord.Type,
        TableDataSource: newRecord.DataSource,
        DataSource: null,
        Type: null,
        ColumnsAdded: newRecord.Columns?.filter(
            (item: any) =>
                Boolean(item) && !oldRecord.Columns?.find((el: any) => el && item.ColumnId?.$oid === el.ColumnId?.$oid),
        ).map((item: any) => ({ ...item, ColumnId: null })),
        ColumnsRemoved: null,
        ColumnsUpdated: newRecord.Columns?.filter(
            (item: any) =>
                Boolean(item) && oldRecord.Columns?.find((el: any) => el && item.ColumnId?.$oid === el.ColumnId?.$oid),
        ).map((item: any) => ({ ...item, ColumnId: null })),
        Columns: null,
        CfId: newRecord.CfId?.$oid,
    };
    const enums: any = {
        DataBasedOn: AuditEnumValues.DataBasedOnType,
    };

    const arrayOfObjects: any = {
        ColumnsRemoved: {
            valueResolver: getColumnValue,
        },
        ColumnsAdded: {
            valueResolver: getColumnValue,
        },
        ColumnsUpdated: {
            valueResolver: getColumnValue,
        },
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            enums,
            arrayOfObjects,
            enumMarkerByField: {
                DataBasedOn: '_DATA_REVIEW_TABLE',
            },
        }),
    );
    return <FieldChangeGrid items={items} />;
};

export { DataReviewTableRecordChangeGrid };
